import { createElement } from 'react';
import { Root } from 'react-dom/client';
import {
  Constructor,
  ILabel,
  INode,
  IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
  SvgVisual,
  Visual,
} from '@ardoq/yfiles';
import { addText } from 'yfilesExtensions/addText';
import { BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS } from '../../consts';
import {
  CollapsibleGraphGroup,
  disposeCallback,
  initializeRoot,
  ensureRoot,
  truncateComponentLabel,
} from '@ardoq/graph';
import {
  BLOCK_DIAGRAM_LABEL_TEXT_PARAMS,
  DATA_RENDER_HASH,
} from 'yfilesExtensions/styles/consts';
import { componentInterface } from '@ardoq/component-interface';
import { GLOBAL_HANDLER_ID_ATTRIBUTE } from 'consts';
import { ensureContrast } from '@ardoq/color-helpers';
import { getComponentCssColors } from 'utils/modelCssManager/getCssColors';
import { colors } from '@ardoq/design-tokens';
import { getCssClassFromDiffType } from '@ardoq/common-helpers';
import { GroupType } from '@ardoq/api-types';
import { getGroupLabelDimensions } from '../GroupBoundsCalculator';
import { GroupHeaderLabel } from '../../GroupHeaderLabel';
import {
  folderStyleGroupLabelStartX,
  getElementOpacityIfTransparentized,
  getGroupHeaderWidthWithoutLabel,
  getGroupLabelMaxWidth,
} from '../../utils';
import { createSvgElement } from '@ardoq/dom-utils';
import { getMultiLabelHash } from './labelUtils';
import { measureExpandedGroupNodeLabelWidth } from './legacyLabelUtils';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { createLabelStyle } from '../shared/styleUtils';
import { LabelExportSupport } from '@ardoq/graph';
import { createMultiLabelExporter, LabelStyleProperties } from '@ardoq/export';

const render = (
  root: Root,
  container: SVGElement,
  label: ILabel,
  getRepresentationData = componentInterface.getRepresentationData
) => {
  const groupNode = label.owner as INode;
  const group = label.owner?.tag as CollapsibleGraphGroup; // using as and not instanceof for performance critical code
  const isComponent = group.isComponent();
  const { type, id, modelId, collapsed, isTransparentized } = group;
  const isWorkspace = type === GroupType.WORKSPACE;
  const representationData = isComponent
    ? getRepresentationData(modelId)
    : null;

  container.setAttribute(DATA_RENDER_HASH, getMultiLabelHash(label));
  if (isComponent) {
    container.setAttribute('class', 'component');
    container.setAttribute(COMPONENT_ID_ATTRIBUTE, modelId);
  } else if (isWorkspace) {
    container.setAttribute('class', 'workspace');
  }
  if (modelId) {
    container.setAttribute(GLOBAL_HANDLER_ID_ATTRIBUTE, modelId);
  } else {
    container.removeAttribute(GLOBAL_HANDLER_ID_ATTRIBUTE);
  }
  container.setAttribute('data-node-id', id);
  const [backgroundFill, foregroundFill] = isComponent
    ? [true, false].map(
        useAsBackgroundStyle =>
          getComponentCssColors(modelId, { useAsBackgroundStyle })?.fill ?? null
      )
    : [colors.grey200, colors.black950];
  const representationColor =
    (backgroundFill && foregroundFill
      ? ensureContrast(backgroundFill, foregroundFill)
      : backgroundFill || foregroundFill) || colors.transparent0;

  const visualDiffType = group.getVisualDiffType();
  const visualDiffClass = getCssClassFromDiffType(visualDiffType);
  const labelX = folderStyleGroupLabelStartX(group);

  if (!isComponent && collapsed) {
    const textElement = createSvgElement('text', {
      opacity: String(getElementOpacityIfTransparentized(isTransparentized)),
    });
    textElement.style.transform = `translateX(${labelX}px)`;
    addText({
      ...BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS,
      targetElement: textElement,
      text: group.getItemLabels()?.mainLabel ?? '',
      maximumSize: new Size(
        getGroupLabelMaxWidth(groupNode),
        BLOCK_DIAGRAM_LABEL_TEXT_PARAMS.maximumSize.height
      ),
    });
    const oldTextElement = container.querySelectorAll('text')[0];
    if (oldTextElement) {
      container.replaceChild(textElement, oldTextElement);
    } else {
      container.appendChild(textElement);
    }
    return;
  }

  const groupLabels = group.getItemLabels();

  if (!groupLabels) {
    return;
  }

  const { mainLabel: labelText, legacyLabelParts } = groupLabels;

  const { groupLabelHeight, groupLabelWidth, groupNameMaxHeight } =
    getGroupLabelDimensions(groupNode);

  const legacyFieldLabelAndValue = legacyLabelParts?.fieldValue
    ? truncateComponentLabel({
        ...legacyLabelParts,
        label: '',
        measure: measureExpandedGroupNodeLabelWidth,
        width: groupNode.layout.width - getGroupHeaderWidthWithoutLabel(group),
      })
    : undefined;

  root.render(
    createElement(GroupHeaderLabel, {
      text: labelText || '',
      fieldLabelAndValue: legacyFieldLabelAndValue,
      groupLabelHeight,
      groupLabelWidth,
      groupNameMaxHeight,
      labelX,
      representationData,
      representationColor: representationData ? representationColor : undefined,
      visualDiffClass,
      isTransparentized,
    })
  );
};

export class MultiLabelGroupStyle extends LabelStyleBase {
  static Instance = new MultiLabelGroupStyle();
  public static getLayout(_context: IRenderContext, label: ILabel): Rect[] {
    const groupNode = label.owner as INode;
    const group = label.owner?.tag as CollapsibleGraphGroup | undefined;
    if (!group) return [];

    const { groupLabelHeight, groupLabelWidth } =
      getGroupLabelDimensions(groupNode);
    const groupLabels = group.getItemLabels();
    if (!groupLabels) return [];

    const labelX = folderStyleGroupLabelStartX(group);

    const labelRect = groupLabels.mainLabel
      ? new Rect(labelX, 0, groupLabelWidth, groupLabelHeight)
      : null;

    return labelRect ? [labelRect] : [];
  }
  public static getStyle(
    _context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties[] | null {
    const group = label.owner?.tag as CollapsibleGraphGroup | undefined;
    if (!group) return null;
    const groupLabels = group.getItemLabels();
    const isTransparentized = !!group.isTransparentized;

    const labelStyleArray = groupLabels?.mainLabel
      ? [
          createLabelStyle(
            BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS.font,
            { kind: 'text', text: groupLabels.mainLabel },
            {
              textAlign: 'left',
              dominantBaseline: 'middle',
              textColor: colors.black950,
              isTransparentized,
            }
          ),
        ]
      : [];

    return labelStyleArray;
  }

  protected constructor(
    private getRepresentationData = componentInterface.getRepresentationData
  ) {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): any {
    if (type === LabelExportSupport) {
      const toAbsolute = (ctx: IRenderContext, lbl: ILabel): Rect[] => {
        const node = lbl.owner as INode | null;
        if (!node) return [];
        const relRects = MultiLabelGroupStyle.getLayout(ctx, lbl);
        return relRects.map(r => new Rect(r.x, r.y, r.width, r.height));
      };

      return {
        toJSON: createMultiLabelExporter(
          toAbsolute,
          MultiLabelGroupStyle.getStyle
        ),
      } as LabelExportSupport;
    }
    return super.lookup(label, type);
  }

  override createVisual(context: IRenderContext, label: ILabel): Visual | null {
    const container = createSvgElement('g');
    render(
      initializeRoot(container),
      container,
      label,
      this.getRepresentationData
    );
    LabelStyleBase.createLayoutTransform(context, label.layout, false).applyTo(
      container
    );
    const result = new SvgVisual(container);
    context.setDisposeCallback(result, disposeCallback);
    return result;
  }
  override updateVisual(
    context: IRenderContext,
    oldVisual: Visual,
    label: ILabel
  ) {
    const container = (oldVisual as SvgVisual).svgElement;
    const hash = container.getAttribute(DATA_RENDER_HASH);
    if (hash !== getMultiLabelHash(label)) {
      render(
        ensureRoot(container),
        container,
        label,
        this.getRepresentationData
      );
    }
    LabelStyleBase.createLayoutTransform(context, label.layout, false).applyTo(
      container
    );
    return oldVisual;
  }
  override getPreferredSize(label: ILabel): Size {
    const groupNode = label.owner as INode;
    const graphGroup: CollapsibleGraphGroup | undefined = label.owner?.tag;

    const isComponent = graphGroup && graphGroup.isComponent();
    if (!graphGroup || (isComponent && graphGroup.collapsed)) {
      const { layout } = groupNode;
      return layout.toSize();
    }

    const { groupLabelHeight } = getGroupLabelDimensions(groupNode);

    return new Size(0, groupLabelHeight);
  }
}
